'use client'

import { Categories } from 'emoji-picker-react'
import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MdOutlineEmojiEmotions } from 'react-icons/md'

// Carregado só quando alguém abre o seletor: o catálogo completo pesa algumas
// centenas de KB, e quem nunca clica no ícone não deve pagar por ele no
// carregamento do sistema.
const EmojiPicker = lazy(() => import('emoji-picker-react'))

/** A biblioteca rotula em inglês; a tela é em português. */
const PICKER_WIDTH = 320

const CATEGORIAS = [
    { category: Categories.SUGGESTED, name: 'Usados recentemente' },
    { category: Categories.SMILEYS_PEOPLE, name: 'Rostos e pessoas' },
    { category: Categories.ANIMALS_NATURE, name: 'Animais e natureza' },
    { category: Categories.FOOD_DRINK, name: 'Comidas e bebidas' },
    { category: Categories.TRAVEL_PLACES, name: 'Viagens e lugares' },
    { category: Categories.ACTIVITIES, name: 'Atividades' },
    { category: Categories.OBJECTS, name: 'Objetos' },
    { category: Categories.SYMBOLS, name: 'Símbolos' },
    { category: Categories.FLAGS, name: 'Bandeiras' },
]

interface EmojiPickerButtonProps {
    /** Recebe o emoji escolhido para inserir no texto. */
    onSelect: (emoji: string) => void
    disabled?: boolean
    className?: string
}

/**
 * Seletor de emoji do campo de mensagem.
 *
 * Emoji já trafegava antes disto: banco, backend e WhatsApp sempre aceitaram, e
 * a equipe mandava 422 por mês usando o teclado do sistema operacional
 * (`Win + .`, `Ctrl+Cmd+Espaço`) — atalho que quase ninguém conhece. O que
 * faltava era o botão, e é só isso que este componente acrescenta.
 */
export default function EmojiPickerButton({
    onSelect,
    disabled,
    className = '',
}: EmojiPickerButtonProps) {
    const [open, setOpen] = useState(false)
    const [position, setPosition] = useState<{ left: number; bottom: number } | null>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    // Clique fora e Esc fecham. Sem isso o painel ficava aberto atrás do campo
    // enquanto a pessoa voltava a digitar.
    useEffect(() => {
        if (!open) return
        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Node
            // O painel vive no `body`, fora do wrapper: sem checar os dois, todo
            // clique dentro dele seria lido como clique fora e fecharia o seletor.
            if (
                !wrapperRef.current?.contains(target) &&
                !panelRef.current?.contains(target)
            ) {
                setOpen(false)
            }
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }
        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open])

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <button
                ref={buttonRef}
                type="button"
                onClick={() => {
                    if (open) {
                        setOpen(false)
                        return
                    }
                    // Medido na hora e preso à JANELA: dentro do chat o painel
                    // ficava atrás do menu e da lista acoplada, que criam seus
                    // próprios contextos de empilhamento — ali nenhum z-index
                    // local resolve.
                    const rect = buttonRef.current?.getBoundingClientRect()
                    if (rect) {
                        setPosition({
                            left: Math.max(8, rect.right - PICKER_WIDTH),
                            bottom: window.innerHeight - rect.top + 8,
                        })
                    }
                    setOpen(true)
                }}
                disabled={disabled}
                aria-expanded={open}
                aria-label="Emojis"
                title="Emojis"
                className="btn btn-ghost btn-sm btn-circle text-base-content/60 hover:text-warning"
            >
                <MdOutlineEmojiEmotions className="text-xl" />
            </button>

            {open &&
                position &&
                createPortal(
                <div
                    // `normal-case` desfaz o capitalize da biblioteca, que em
                    // português vira "Rostos E Pessoas" — com o "E" maiúsculo.
                    // `z-[10000]`: acima do modal de conversa aberto pelo funil
                    // (`chat-popup.tsx`, z-index 9999), que também vive no `body`.
                    className="fixed z-[10000] shadow-2xl rounded-lg overflow-hidden [&_.epr-emoji-category-label]:!normal-case"
                    style={{ left: position.left, bottom: position.bottom }}
                    ref={panelRef}
                >
                    <Suspense
                        fallback={
                            <div className="flex h-80 w-[320px] items-center justify-center rounded-lg border border-base-300 bg-base-100">
                                <span className="loading loading-spinner loading-md text-warning" />
                            </div>
                        }
                    >
                        <EmojiPicker
                            onEmojiClick={emojiData => {
                                onSelect(emojiData.emoji)
                                // O painel continua aberto: mandar três emojis
                                // seguidos é comum, e fechar a cada escolha
                                // obrigaria a reabrir toda vez.
                            }}
                            // O painel é claro porque o tema do sistema é um só
                            // e claro (`use-theme`): seguir o tema do sistema
                            // operacional deixaria o seletor escuro dentro de
                            // uma tela branca.
                            searchPlaceholder="Buscar emoji..."
                            categories={CATEGORIAS}
                            width={PICKER_WIDTH}
                            height={400}
                            previewConfig={{ showPreview: false }}
                            lazyLoadEmojis
                        />
                    </Suspense>
                </div>,
                document.body,
            )}
        </div>
    )
}
